import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
    TRANSPORT_ADMIN,
    TRANSPORT_COORDINATOR,
    TRANSPORT_NURSE,
    TRANSPORT_VIEW_ONLY,
} from '../../components/dashboard/transports/constants';
import { useTabs } from '../../context/TabContext';
import { resetState } from '../../redux/slices/orderGuides';
import { useScopeCheck } from '../../reusable-components/scopes';
import { fNumber } from '../../utils/formatNumber';

import {
    isAwaitingSubmissionInvoice,
    isInvoiceInQueue,
    isRejectedInvoice,
    isUserPendingInvoice,
} from '../../utils/helpers/invoicingHelper';

export function useHeaderItems() {
    const isAdmin = useScopeCheck([], true);
    const isItemCatalogView = useScopeCheck(['Item-Catalog-View'], true);
    const isViewOrderGuide = useScopeCheck(['View-OrderGuide'], true);
    const isReviewOrder = useScopeCheck(['Edit-ReviewWorkflow'], true);
    const isViewBudgets = useScopeCheck(['View-Budgets'], true);
    const isViewCensus = useScopeCheck(['View-Census'], true);
    const isInvoiceView = useScopeCheck(['Invoice-View', 'ManualInvoice-View'], true);
    const isFacilityApproversEdit = useScopeCheck(['FacilityApprovers-Edit', 'ElectronicFacilityApprovers-Edit'], true);
    const isViewSystemNotices = useScopeCheck(['View-SystemNotices'], false, true);
    const isViewFacilityAccounts = useScopeCheck(['View-Facility-Accounts']);

    const { clearFiltersAndNavigate } = useTabs();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const { unreadCount } = useSelector((state) => state.tickets);
    const { unreadOrders } = useSelector((state) => state.orders);
    const { unreadRentals } = useSelector((state) => state.rentals);
    const {
        data: { pendingCount, manualInvoicing, pendingInvoicing },
    } = useSelector((state) => state.invoicing);
    const { user } = useSelector((state) => state.user);

    const formatCount = (num) => (num > 0 ? (num > 99 ? '(99+)' : `(${fNumber(num)})`) : '');

    const checkPath = (path) => pathname.includes(`/dashboard/${path}`);

    const pendingFilterCount = useMemo(() => pendingInvoicing?.filter((invoice) => isUserPendingInvoice(invoice, user.email))?.length, [pendingInvoicing, user]);

    const awaitingSubmitCount = useMemo(
        () => pendingInvoicing?.filter((invoice) => isAwaitingSubmissionInvoice(invoice))?.length,
        [pendingInvoicing]
    );

    const invoicesInQueueCount = useMemo(
        () => pendingInvoicing?.filter((invoice) => isInvoiceInQueue(invoice))?.length,
        [pendingInvoicing]
    );

    const rejectedInvoicesCount = useMemo(
        () => pendingInvoicing?.filter((invoice) => isRejectedInvoice(invoice))?.length,
        [pendingInvoicing]
    );

    let adminSubitems = [
        {
            scopes: ['Item-Catalog-View'],
            label: 'Catalog',
            onClick: () => clearFiltersAndNavigate('/dashboard/admin/catalog'),
            path: 'admin/catalog',
            allowAdmin: true,
        },
        {
            scopes: ['View-OrderGuide'],
            label: 'Order Guides',
            onClick: async () => {
                dispatch(resetState());
                clearFiltersAndNavigate('/dashboard/admin/order-guides');
            },
            path: 'admin/order-guides',
            allowAdmin: true,
        },
        {
            scopes: ['Edit-ReviewWorkflow'],
            allowAdmin: true,
            label: 'Facilities',
            onClick: () => clearFiltersAndNavigate('/dashboard/admin/facilities'),
            path: 'admin/facilities',
        },
        {
            scopes: ['View-Budgets'],
            label: 'Budgets',
            onClick: () => clearFiltersAndNavigate('/dashboard/admin/budgets'),
            path: 'admin/budgets',
            allowAdmin: true,
        },
        isAdmin
            ? {
                scopes: ['Admin'],
                allowAdmin: true,
                label: 'Vendors',
                onClick: () => clearFiltersAndNavigate('/dashboard/admin/vendors'),
                path: 'admin/vendors',
            }
            : {
                scopes: ['View-Facility-Accounts'],
                label: 'Facility Accounts',
                onClick: () => clearFiltersAndNavigate('/dashboard/admin/facility-accounts'),
                path: 'admin/facility-accounts',
            },
        {
            scopes: ['View-Census'],
            label: 'Census',
            onClick: () => clearFiltersAndNavigate('/dashboard/admin/census'),
            path: 'admin/census',
            allowAdmin: true,
        },
        {
            scopes: ['User-View'],
            label: 'Users',
            onClick: () => clearFiltersAndNavigate('/dashboard/admin/users'),
            path: 'admin/users',
            allowAdmin: true,
        },
        {
            scopes: ['Admin'],
            allowAdmin: true,
            label: 'Purchasing Categories',
            onClick: () => clearFiltersAndNavigate('/dashboard/admin/purchasing-categories'),
            path: 'admin/purchasing-categories',
        },
        {
            scopes: ['View-SentEmails', 'Super-Admin'],
            label: 'Sent Emails',
            onClick: () =>
                clearFiltersAndNavigate(
                    `/dashboard/admin/emails?start=${new Date(
                        new Date().setDate(new Date().getDate() - 6)
                    ).toISOString()}&end=${new Date().toISOString()}`
                ),
            path: 'admin/emails',
        },
        {
            scopes: ['View-ReceivedEmails', 'Super-Admin'],
            label: 'Received Emails',
            onClick: () =>
                clearFiltersAndNavigate(
                    `/dashboard/admin/received-emails?start=${new Date(
                        new Date().setDate(new Date().getDate() - 6)
                    ).toISOString()}&end=${new Date().toISOString()}`
                ),
            path: 'admin/received-emails',
        },
        {
            scopes: ['View-SystemNotices', 'Super-Admin'],
            label: 'System Notices',
            onClick: () => clearFiltersAndNavigate('/dashboard/admin/system-notices'),
            path: 'admin/system-notices',
        },
    ];

    let superAdminSubitems = [
        {
            scopes: ['Super-Admin'],
            label: 'Email Recipients',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/email-recipients'),
            path: 'super-admin/email-recipients',
        },
        {
            scopes: ['Super-Admin'],
            label: 'Facilities',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/facilities'),
            path: 'super-admin/facilities',
        },
        {
            scopes: ['Super-Admin'],
            label: 'Site Rules',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/site-rules'),
            path: 'super-admin/site-rules',
        },
        {
            scopes: ['Super-Admin'],
            label: 'System Notices',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/system-notices'),
            path: 'super-admin/system-notices',
        },
        {
            scopes: ['Super-Admin'],
            label: 'Booking Types',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/invoice-booking-types'),
            path: 'super-admin/invoice-booking-types',
        },
        {
            scopes: ['Super-Admin'],
            label: 'Tasks',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/tasks'),
            path: 'super-admin/tasks',
        },
        {
            scopes: ['Super-Admin'],
            label: 'Groups',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/groups'),
            path: 'super-admin/groups',
        },
        {
            scopes: ['Super-Admin'],
            label: 'Excel Upload',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/excel'),
            path: 'super-admin/excel',
        },
        {
            scopes: ['Super-Admin'],
            label: 'Shop',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/vendor-catalog'),
            path: 'super-admin/vendor-catalog',
        },
        {
            scopes: ['Super-Admin'],
            label: 'Global Vendors',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/warehouse-vendors'),
            path: 'super-admin/warehouse-vendors',
        },
        {
            scopes: ['Super-Admin'],
            label: 'Price Accounts',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/price-accounts'),
            path: 'super-admin/price-accounts',
        },
        {
            scopes: ['Super-Admin'],
            label: 'Reports',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/reports'),
            path: 'super-admin/reports',
        },
    ];

    let invoiceSubitems = [
        {
            scopes: ['Manual-Invoice'],
            label: 'New Invoices',
            onClick: () => clearFiltersAndNavigate('/dashboard/invoicing/new'),
            badgeContent: manualInvoicing?.length > 0 ? fNumber(manualInvoicing.length) : null,
            path: 'invoicing/new',
            allowAdmin: true,
        },
        {
            scopes: ['Invoice-View', 'Manual-Invoice', 'ManualInvoice-View', 'ManualInvoice-Edit', 'ManualInvoice-Submit'],
            label: 'All Pending Invoices',
            onClick: () => clearFiltersAndNavigate('/dashboard/invoicing/pending'),
            badgeContent: pendingInvoicing?.length > 0 ? fNumber(pendingInvoicing.length) : null,
            path: 'invoicing/pending',
            allowAdmin: true,
        },
        {
            scopes: [
                'Facility-Manager',
                'Manual-Invoice',
                'ManualInvoice-View',
                'ManualInvoice-Edit',
                'ManualInvoice-Submit',
            ],
            label: 'My Pending Invoices',
            onClick: () => clearFiltersAndNavigate('/dashboard/invoicing/pendingFilter'),
            badgeContent: pendingFilterCount > 0 ? fNumber(pendingFilterCount) : null,
            path: 'invoicing/pendingFilter',
            allowAdmin: true,
        },
        {
            scopes: ['Manual-Invoice', 'ManualInvoice-View', 'ManualInvoice-Edit', 'ManualInvoice-Submit'],
            label: 'Waiting to be Submitted',
            onClick: () => clearFiltersAndNavigate('/dashboard/invoicing/awaitingSubmission'),
            badgeContent: awaitingSubmitCount > 0 ? fNumber(awaitingSubmitCount) : null,
            path: 'invoicing/awaitingSubmission',
            allowAdmin: true,
        },
        {
            scopes: ['Manual-Invoice', 'ManualInvoice-View', 'ManualInvoice-Edit', 'ManualInvoice-Submit'],
            label: 'Invoices in Queue',
            onClick: () => clearFiltersAndNavigate('/dashboard/invoicing/invoicesInQueue'),
            badgeContent: invoicesInQueueCount > 0 ? fNumber(invoicesInQueueCount) : null,
            path: 'invoicing/invoicesInQueue',
            allowAdmin: true,
        },
        {
            scopes: ['Manual-Invoice', 'ManualInvoice-View', 'ManualInvoice-Edit', 'ManualInvoice-Submit'],
            label: 'Rejected Invoices',
            onClick: () => clearFiltersAndNavigate('/dashboard/invoicing/rejectedInvoices'),
            badgeContent: rejectedInvoicesCount > 0 ? fNumber(rejectedInvoicesCount) : null,
            path: 'invoicing/rejectedInvoices',
            allowAdmin: true,
        },
        {
            scopes: ['Invoice-View', 'Manual-Invoice', 'ManualInvoice-View', 'ManualInvoice-Edit', 'ManualInvoice-Submit'],
            label: 'Processed Invoices',
            onClick: () =>
                clearFiltersAndNavigate(
                    `/dashboard/invoicing/previous?start=${new Date(
                        new Date().setDate(new Date().getDate() - 29)
                    ).toISOString()}&end=${new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString()}`
                ),
            path: 'invoicing/previous',
            allowAdmin: true,
        },
        {
            scopes: ['Super-Admin', 'View-Invoice-GLs', 'Edit-Invoice-GLs', 'View-Invoice-Vendors', 'Edit-Invoice-Vendors'],
            label: 'Vendors & GLs',
            onClick: () => clearFiltersAndNavigate('/dashboard/invoicing/vendors'),
            path: 'invoicing/vendors',
            allowAdmin: true,
        },
        {
            scopes: ['Super-Admin', 'FacilityApprovers-Edit', 'ElectronicFacilityApprovers-Edit'],
            label: 'Settings',
            onClick: () => clearFiltersAndNavigate('/dashboard/invoicing/settings'),
            path: 'invoicing/settings',
        },
    ];

    let ticketSubitems = [
        {
            scopes: ['Super-Admin', 'Ticket-Settings-Access'],
            label: 'Tickets',
            onClick: () => clearFiltersAndNavigate('/dashboard/tickets?status=active'),
            path: 'tickets',
        },
        {
            scopes: ['Super-Admin', 'Ticket-Settings-Access'],
            label: 'Settings',
            path: 'tickets/settings',
            onClick: () => clearFiltersAndNavigate('/dashboard/tickets/settings'),
        },
    ];

    let rentalSubitems = [
        {
            scopes: ['Rental-Order-View'],
            label: 'Pending',
            path: 'rentals/pending-rentals',
            allowAdmin: true,
            onClick: () => clearFiltersAndNavigate('/dashboard/rentals/pending-rentals'),
        },
        {
            scopes: ['Rental-Order-View'],
            label: 'Current',
            path: 'rentals/current-rentals',
            allowAdmin: true,
            onClick: () => clearFiltersAndNavigate('/dashboard/rentals/current-rentals'),
        },
        {
            scopes: ['Rental-Order-View'],
            label: 'Previous',
            path: 'rentals/previous-rentals',
            onClick: () => clearFiltersAndNavigate('/dashboard/rentals/previous-rentals'),
            allowAdmin: true,
        },
        {
            scopes: ['Rental-Catalog-View'],
            label: 'Catalog',
            path: 'rentals/rental-catalog',
            onClick: () => clearFiltersAndNavigate('/dashboard/rentals/rental-catalog'),
            allowAdmin: true,
        },
        {
            scopes: ['Rental-Vendor-View'],
            label: 'Vendors',
            path: 'rentals/rental-vendors',
            onClick: () => clearFiltersAndNavigate('/dashboard/rentals/rental-vendors'),
            allowAdmin: true,
        },
        {
            scopes: ['Rental-Settings-View'],
            label: 'Settings',
            path: 'rentals/rental-settings',
            onClick: () => clearFiltersAndNavigate('/dashboard/rentals/rental-settings'),
            allowAdmin: true,
        },
    ];

    let transportSubitems = [
        {
            scopes: ['Super-Admin', TRANSPORT_ADMIN, TRANSPORT_COORDINATOR, TRANSPORT_NURSE, TRANSPORT_VIEW_ONLY],
            label: 'Calendar',
            onClick: () => clearFiltersAndNavigate('/dashboard/transports/calendar'),
            path: 'transports/calendar',
        },
        {
            scopes: ['Super-Admin', TRANSPORT_ADMIN, TRANSPORT_COORDINATOR, TRANSPORT_NURSE],
            label: 'Settings',
            onClick: () => clearFiltersAndNavigate('/dashboard/transports/settings'),
            path: 'transports/settings',
        },
    ];

    let contractSubitems = [
        {
            scopes: ['Contracts-View', 'Contracts-Edit', 'Admin', 'Super-Admin'],
            label: 'Active',
            onClick: () => clearFiltersAndNavigate('/dashboard/contracts/active'),
            path: 'contracts/active',
            allowAdmin: false,
        },
        {
            scopes: ['Contracts-View', 'Contracts-Edit', 'Admin', 'Super-Admin'],
            label: 'Terminated',
            onClick: () => clearFiltersAndNavigate('/dashboard/contracts/terminated'),
            path: 'contracts/terminated',
            allowAdmin: false,
        },
        {
            scopes: ['Contracts-View', 'Contracts-Edit', 'Admin', 'Super-Admin'],
            label: 'Previous Owner',
            onClick: () => clearFiltersAndNavigate('/dashboard/contracts/previous-owner'),
            path: 'contracts/previous-owner',
            allowAdmin: false,
        },
        {
            scopes: ['Contracts-View', 'Contracts-Edit', 'Admin', 'Super-Admin'],
            label: 'Pending Renew',
            onClick: () => clearFiltersAndNavigate('/dashboard/contracts/pending-renew'),
            path: 'contracts/pending-renew',
            allowAdmin: false,
        },
        {
            scopes: ['Contracts-View', 'Super-Admin'],
            label: 'AI Tool',
            onClick: async () => {
                dispatch(resetState());
                clearFiltersAndNavigate('/dashboard/contracts/ai-tool');
            },
            path: 'contracts/ai-tool',
            allowAdmin: false,
        },
    ]
    const getBestMatchingSubitem = (subitems) => {
        let best = null;
        let bestLength = -1;
        subitems.forEach((item) => {
            const full = `/dashboard/${item.path}`;
            if (pathname === full || pathname.startsWith(`${full}/`)) {
                if (full.length > bestLength) {
                    best = item;
                    bestLength = full.length;
                }
            }
        });
        return best;
    };

    const activeItem = getBestMatchingSubitem([
        ...adminSubitems,
        ...superAdminSubitems,
        ...ticketSubitems,
        ...invoiceSubitems,
        ...rentalSubitems,
        ...transportSubitems,
        ...contractSubitems,
    ]);
    const addActiveItem = (subitems) =>
        subitems.map((item) => ({
            ...item,
            textDecoration: item.path === activeItem?.path ? 'underline' : 'none',
            color: item.path === activeItem?.path ? 'primary' : 'text.secondary',
        }));

    adminSubitems = addActiveItem(adminSubitems);
    superAdminSubitems = addActiveItem(superAdminSubitems);
    ticketSubitems = addActiveItem(ticketSubitems);
    invoiceSubitems = addActiveItem(invoiceSubitems);
    rentalSubitems = addActiveItem(rentalSubitems);
    transportSubitems = addActiveItem(transportSubitems);
    contractSubitems = addActiveItem(contractSubitems);

    const headerItems = [
        {
            scopes: ['View-Order'],
            allowAdmin: true,
            path: 'orders',
            color: checkPath('orders') ? 'primary' : 'text.secondary',
            onClick: () => clearFiltersAndNavigate('/dashboard/orders?filter=pending'),
            label: `Orders ${formatCount(unreadOrders)}`,
        },
        {
            scopes: [],
            color: checkPath('tickets') ? 'primary' : 'text.secondary',
            path: 'tickets',
            onClick: () => clearFiltersAndNavigate('/dashboard/tickets?status=active'),
            label: `Tickets ${formatCount(unreadCount)}`,
            subitems: ticketSubitems,
        },
        {
            scopes: [
                'Invoice-View',
                'ManualInvoice-View',
                'View-Invoice-Vendors',
                'Edit-Invoice-Vendors',
                'View-Invoice-GLs',
                'Edit-Invoice-GLs',
                'FacilityApprovers-Edit',
                'ElectronicFacilityApprovers-Edit',
            ],
            allowAdmin: true,
            color: checkPath('invoicing') ? 'primary' : 'text.secondary',
            path: 'invoicing',
            onClick: () =>
                clearFiltersAndNavigate(
                    isInvoiceView
                        ? '/dashboard/invoicing/pendingFilter'
                        : isFacilityApproversEdit
                            ? 'dashboard/invoicing/settings'
                            : 'dashboard/invoicing/vendors'
                ),
            label: `Invoicing ${formatCount(pendingCount)}`,
            subitems: invoiceSubitems,
        },
        {
            scopes: [
                'Item-Catalog-View',
                'View-OrderGuide',
                'Edit-ReviewWorkflow',
                'View-Budgets',
                'View-Census',
                'User-View',
                'View-SystemNotices',
            ],
            allowAdmin: true,
            color: checkPath('admin') ? 'primary' : 'text.secondary',
            path: 'admin',
            onClick: () =>
                clearFiltersAndNavigate(
                    `/dashboard/admin/${isAdmin || isItemCatalogView
                        ? 'catalog'
                        : isViewOrderGuide
                            ? 'order-guides'
                            : isViewBudgets
                                ? 'budgets'
                                : isViewFacilityAccounts
                                    ? 'facility-accounts'
                                    : isViewCensus
                                        ? 'census'
                                        : isReviewOrder
                                            ? 'facilities'
                                            : isViewSystemNotices
                                                ? 'system-notices'
                                                : 'users'
                    }`
                ),
            label: 'Admin',
            subitems: adminSubitems,
        },
        {
            scopes: ['Super-Admin'],
            color: checkPath('super-admin') ? 'primary' : 'text.secondary',
            path: 'super-admin',
            onClick: () => clearFiltersAndNavigate('/dashboard/super-admin/email-recipients'),
            label: 'Super Admin',
            subitems: superAdminSubitems,
        },
        {
            scopes: ['View-PurchasingReports', 'View-InvoiceReports', 'View-RentalReports', 'View-TransportReports'],
            allowAdmin: true,
            color: checkPath('reports') ? 'primary' : 'text.secondary',
            path: 'reports',
            onClick: () => clearFiltersAndNavigate(`/dashboard/reports`),
            label: 'Reports',
        },
        {
            scopes: ['Rental-Order-View'],
            allowAdmin: true,
            color: checkPath('rentals') ? 'primary' : 'text.secondary',
            path: 'rentals',
            onClick: () => clearFiltersAndNavigate('/dashboard/rentals/pending-rentals'),
            label: `Rentals ${formatCount(unreadRentals)}`,
            subitems: rentalSubitems,
        },
        {
            scopes: ['Super-Admin', 'Transition-Admin', 'Transition-User-View'],
            color: checkPath('transitions') ? 'primary' : 'text.secondary',
            path: 'transitions',
            onClick: () => clearFiltersAndNavigate('/dashboard/transitions/home'),
            label: 'Transitions',
            badge: true,
            badgeContent: 'Beta',
        },
    ];
    if (user?.siteId === 1 || user?.siteId === 2) {
        headerItems.push({
            scopes: ['Super-Admin', TRANSPORT_ADMIN, TRANSPORT_COORDINATOR, TRANSPORT_NURSE, TRANSPORT_VIEW_ONLY],
            color: checkPath('transports') ? 'primary' : 'text.secondary',
            path: 'Transports',
            onClick: () => clearFiltersAndNavigate('/dashboard/transports/calendar'),
            label: 'Transports',
            badge: true,
            badgeContent: 'Beta',
            subitems: transportSubitems,
        });
        headerItems.push({
            scopes: ['Super-Admin', 'Admin', 'Contracts-View', 'Contracts-Edit'],
            color: checkPath('contracts') ? 'primary' : 'text.secondary',
            path: 'contracts',
            onClick: () => clearFiltersAndNavigate('/dashboard/contracts/active'),
            label: 'Contracts',
            badge: true,
            badgeContent: 'Beta',
            subitems: contractSubitems,
        });
    }
    return {
        headerItems,
        adminSubitems,
        ticketSubitems,
        invoiceSubitems,
        superAdminSubitems,
        rentalSubitems,
        transportSubitems,
        contractSubitems,
        checkPath,
    };
}
